import { PlanningAssignment, ResourceExternalEvent } from '../../../Models/resource.models';
import { ResourceAbsencePlanning } from '../../../Models/resource-modules.models';

/**
 * Einträge von „Mein Kalender“. Alle Zeiten sind lokale Wandzeiten
 * (yyyy-MM-dd / HH:mm) – wie in der Teamplanung werden Outlook-Zeitstempel
 * per Teilstring gelesen, nicht über Date/Zeitzonen umgerechnet.
 */
export type MyCalendarItemKind = 'assignment' | 'outlook' | 'absence';

export interface MyCalendarItem {
  key: string;
  kind: MyCalendarItemKind;
  title: string;
  /** Kategorie bzw. „Outlook-Vorschlag“ / Abwesenheitsart. */
  label: string;
  color: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  /** Ganztägig oder mehrtägig – in der Wochenansicht in der Ganztagszeile. */
  allDay: boolean;
  location?: string | null;
  customerName?: string | null;
  /** Interner Termin darf hier geändert werden (selbst angelegt). */
  editable: boolean;
  assignment?: PlanningAssignment;
  external?: ResourceExternalEvent;
  absence?: ResourceAbsencePlanning;
}

/** Ein ausgewählter Zeitraum (Klick bzw. Ziehen auf eine freie Fläche). */
export interface MyCalendarSlot {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export const OUTLOOK_COLOR = '#94a3b8';
export const ABSENCE_COLOR = '#7b1fa2';

// ---- Datum (lokal, ohne Zeitzonen) ------------------------------------------------

export function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(key: string, days: number): string {
  const date = parseDateKey(key);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

/** Montag der Woche, in der das Datum liegt. */
export function startOfWeek(key: string): string {
  const date = parseDateKey(key);
  return addDays(key, -((date.getDay() + 6) % 7));
}

export function minutesOf(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function timeOf(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, minutes));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/** Berührt der Eintrag den Tag? */
export function coversDay(item: MyCalendarItem, day: string): boolean {
  return item.startDate <= day && item.endDate >= day;
}

// ---- Umwandlung der Datenquellen ------------------------------------------------------

export function fromAssignment(a: PlanningAssignment): MyCalendarItem {
  return {
    key: `a-${a.id}`,
    kind: 'assignment',
    title: a.title,
    label: a.category || 'Planungstermin',
    color: a.color || '#2563eb',
    startDate: a.startDate,
    startTime: a.startTime,
    endDate: a.endDate,
    endTime: a.endTime,
    allDay: a.startDate !== a.endDate || (a.startTime === '00:00' && a.endTime >= '23:59'),
    location: a.location,
    editable: !!a.canEdit,
    assignment: a
  };
}

/** Outlook-Vorschlag; bei ganztägigen iCal-Terminen ist das Enddatum exklusiv. */
export function fromExternal(e: ResourceExternalEvent): MyCalendarItem {
  const startDate = e.start.substring(0, 10);
  let endDate = e.end.substring(0, 10);
  let endTime = e.end.substring(11, 16) || '23:59';
  if (e.allDay) {
    endDate = addDays(endDate, -1);
    if (endDate < startDate) endDate = startDate;
    endTime = '23:59';
  } else if (endTime === '00:00' && endDate > startDate) {
    endDate = addDays(endDate, -1);
    endTime = '23:59';
  }
  return {
    key: `o-${e.userId}|${e.uid}|${e.start}`,
    kind: 'outlook',
    title: e.title || 'Outlook-Termin',
    label: 'Outlook-Vorschlag',
    color: OUTLOOK_COLOR,
    startDate,
    startTime: e.allDay ? '00:00' : (e.start.substring(11, 16) || '00:00'),
    endDate,
    endTime,
    allDay: e.allDay || startDate !== endDate,
    editable: false,
    external: e
  };
}

export function fromAbsence(a: ResourceAbsencePlanning): MyCalendarItem {
  const startDate = a.start.substring(0, 10);
  let endDate = a.end.substring(0, 10);
  let endTime = a.end.substring(11, 16) || '23:59';
  if (endTime === '00:00' && endDate > startDate) {
    endDate = addDays(endDate, -1);
    endTime = '23:59';
  }
  const startTime = a.start.substring(11, 16) || '00:00';
  return {
    key: `u-${a.id}`,
    kind: 'absence',
    title: a.title || a.categoryName || 'Abwesenheit',
    label: a.categoryName || 'Abwesenheit',
    color: a.color || ABSENCE_COLOR,
    startDate,
    startTime,
    endDate,
    endTime,
    allDay: startDate !== endDate || (startTime === '00:00' && endTime >= '23:59'),
    editable: false,
    absence: a
  };
}

export function compareItems(a: MyCalendarItem, b: MyCalendarItem): number {
  return `${a.startDate}T${a.startTime}`.localeCompare(`${b.startDate}T${b.startTime}`) ||
    a.title.localeCompare(b.title, 'de');
}

/** Angaben für den gemeinsamen Termin-Tooltip (gleiche Inhalte wie in der Teamplanung). */
export interface MyCalendarTooltip {
  category: string;
  utilizationPercent: number | null;
  allDay: boolean;
  footerText: string | null;
  footerIcon: string;
}

export function tooltipFor(item: MyCalendarItem): MyCalendarTooltip {
  switch (item.kind) {
    case 'outlook':
      return {
        category: 'Outlook-Termin',
        utilizationPercent: null,
        allDay: !!item.external?.allDay,
        footerText: 'Noch nicht in die Planung übernommen',
        footerIcon: 'info_outline'
      };
    case 'absence':
      return {
        category: item.label || 'Abwesenheit',
        utilizationPercent: null,
        allDay: false,
        footerText: 'Genehmigte Abwesenheit',
        footerIcon: 'beach_access'
      };
    default: {
      const a = item.assignment;
      return {
        category: item.label || 'Termin',
        utilizationPercent: a?.participants[0]?.utilizationPercent ?? null,
        allDay: false,
        footerText: !item.editable
          ? 'Dir zugewiesen – nur Ansicht'
          : a?.sourceExternalUid ? 'Aus Outlook in die Planung übernommen' : null,
        footerIcon: !item.editable ? 'lock_outline' : 'event_available'
      };
    }
  }
}
